const DEFAULT_PRODUCT_ID = '16198128763215';
const DEFAULT_ONE_TIME_DEAL_ID = 'b79c7438-6dc7-4f7e-b3e0-cb5766ee3ea9';
const UNLISTED_PRODUCT_ID = '16290387460431';
const UNLISTED_ONE_TIME_DEAL_ID = 'b8ae4305-ed9e-4961-ba56-aadab74b5388';
const DIRECT_CHECKOUT_FORM_SELECTOR = 'form[action*="/cart/add"]:has(input[name="return_to"][value="/checkout"])';
const ONE_TIME_PROPERTY_NAME = 'Purchase option';
const UNLISTED_TEMPLATE = 'page.rhodiola-rosea';
const initializedWidgets = new WeakSet();
const scheduledWidgets = new WeakSet();
const defaultedWidgets = new WeakSet();
const forcingSubscriptionWidgets = new WeakSet();

let oneTimeCheckoutPending = false;

function parseJsonAttribute(element, name) {
  try {
    return JSON.parse(element.getAttribute(name) || '{}');
  } catch (_error) {
    return {};
  }
}

function formatMoney(cents, moneyFormat) {
  const amount = (cents / 100).toFixed(2);
  const format = moneyFormat || '£{{amount}}';

  return format
    .replace(/{{\s*amount_no_decimals\s*}}/g, String(Math.round(cents / 100)))
    .replace(/{{\s*amount\s*}}/g, amount);
}

function getOneTimeLabels(widget, dealBar) {
  const product = parseJsonAttribute(widget, 'product');
  const config = parseJsonAttribute(widget, 'config');
  const variant = product.variants?.find((item) => item.id === product.selectedVariantId) || product.variants?.[0];
  const quantity = Number(dealBar.buyQuantity) || 1;
  const noun = quantity === 1 ? 'bottle' : 'bottles';

  if (!variant) {
    return {
      title: `${quantity} ${quantity === 1 ? 'Bottle' : 'Bottles'}`,
      subtitle: `${quantity} ${noun} total`,
    };
  }

  const unitPrice = Number(variant.price) || 0;
  const unitCompareAtPrice = Number(variant.compareAtPrice) || unitPrice;
  const savings = Math.max(0, unitCompareAtPrice - unitPrice) * quantity;
  const savingsText = savings > 0 ? ` | Save ${formatMoney(savings, config.moneyFormat)}` : '';

  return {
    title: `${quantity} ${quantity === 1 ? 'Bottle' : 'Bottles'}`,
    subtitle: `${quantity} ${noun} total${savingsText}`,
  };
}

function getTargetWidget() {
  return document.querySelector(
    `kaching-bundle[product-id="${getTargetProductId()}"] kaching-bundles-block`
  );
}

function getSubscriptionToggle(widget) {
  return widget?.querySelector('.kaching-bundles__subscriptions [role="checkbox"][aria-checked]');
}

function isOneTimePurchase(widget) {
  return getSubscriptionToggle(widget)?.getAttribute('aria-checked') === 'false';
}

function isUnlistedSubscriptionPage() {
  return document.querySelector(`main[data-template="${UNLISTED_TEMPLATE}"]`) !== null;
}

function getTargetProductId() {
  return isUnlistedSubscriptionPage() ? UNLISTED_PRODUCT_ID : DEFAULT_PRODUCT_ID;
}

function getOneTimeDealId() {
  return isUnlistedSubscriptionPage() ? UNLISTED_ONE_TIME_DEAL_ID : DEFAULT_ONE_TIME_DEAL_ID;
}

function enforcePurchaseMode(widget) {
  if (!isUnlistedSubscriptionPage()) {
    setOneTimeAsDefault(widget);
    return;
  }

  const toggle = getSubscriptionToggle(widget);
  if (!toggle || toggle.getAttribute('aria-checked') === 'true') return;
  if (forcingSubscriptionWidgets.has(widget)) return;

  forcingSubscriptionWidgets.add(widget);
  toggle.click();

  setTimeout(() => {
    forcingSubscriptionWidgets.delete(widget);
    scheduleSync(widget);
  }, 150);
}

function setOneTimeAsDefault(widget) {
  if (defaultedWidgets.has(widget)) return;

  const toggle = getSubscriptionToggle(widget);
  if (!toggle) return;

  // Apply this only once when the Kaching widget first becomes ready. After
  // that, the customer's own subscription choice is never overridden.
  defaultedWidgets.add(widget);
  if (toggle.getAttribute('aria-checked') === 'true') toggle.click();
}

function getSelectedDealBar(widget) {
  const selectedBar = widget?.querySelector(
    '.kaching-bundles__bar--selected[data-deal-bar-id]'
  );

  if (!selectedBar) return;

  const dealBlock = parseJsonAttribute(widget, 'deal-block');
  return (dealBlock.dealBars || []).find((dealBar) => dealBar.id === selectedBar.dataset.dealBarId);
}

function getSelectedVariant(widget) {
  const product = parseJsonAttribute(widget, 'product');
  return product.variants?.find((variant) => variant.id === product.selectedVariantId) || product.variants?.[0];
}

function bottlesInDealBar(dealBar) {
  const paid = Number(dealBar.quantity ?? dealBar.buyQuantity) || 1;
  const free = Number(dealBar.getQuantity) || 0;
  return paid + free;
}

function syncRefillNote(widget, dealBars) {
  const note = widget.closest('.phs')?.querySelector('.phs__refill-note');
  const isSubscribed = getSubscriptionToggle(widget)?.getAttribute('aria-checked') === 'true';

  if (note) {
    note.hidden = !isSubscribed;
    note.setAttribute('aria-hidden', String(!isSubscribed));
  }

  if (!isSubscribed) return;

  const selectedBar = widget.querySelector(
    '.kaching-bundles__bar--selected[data-deal-bar-id]'
  );

  if (!note || !selectedBar) return;

  const dealBar = dealBars.get(selectedBar.dataset.dealBarId);
  if (!dealBar) return;

  const text =
    note.getAttribute(`data-refill-${bottlesInDealBar(dealBar)}`) ||
    note.getAttribute('data-refill-default') ||
    '';

  if (note.textContent !== text) note.textContent = text;
}

async function goToOneTimeCheckout(form, widget) {
  if (oneTimeCheckoutPending) return;

  const dealBar = getSelectedDealBar(widget);
  const variant = getSelectedVariant(widget);

  if (!dealBar || !variant) return;

  oneTimeCheckoutPending = true;

  const button = form.querySelector('.phs__btn');
  const originalButtonText = button?.textContent;
  const quantity = Math.max(1, Number(dealBar.buyQuantity || dealBar.quantity) || 1);
  const optionTitle = getOneTimeLabels(widget, { ...dealBar, buyQuantity: quantity }).title;

  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  try {
    const clearResponse = await fetch(Theme.routes.cart_clear_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    if (!clearResponse.ok) throw new Error(`Unable to clear cart (${clearResponse.status})`);

    // The query parameter tells Kaching not to transform this intentional
    // one-time item into its active BOGO deal.
    const separator = Theme.routes.cart_add_url.includes('?') ? '&' : '?';
    const addResponse = await fetch(
      `${Theme.routes.cart_add_url}${separator}kaching_bundles=true`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variant.id),
              quantity,
              properties: {
                [ONE_TIME_PROPERTY_NAME]: optionTitle,
                __kaching_bundles: JSON.stringify({
                  deal: getOneTimeDealId(),
                  main: true,
                }),
              },
            },
          ],
        }),
      }
    );

    if (!addResponse.ok) throw new Error(`Unable to add item (${addResponse.status})`);

    window.location.assign('/checkout');
  } catch (error) {
    console.error('[kaching-one-time-checkout] Unable to start checkout:', error);
    oneTimeCheckoutPending = false;

    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (originalButtonText) button.textContent = originalButtonText;
    }
  }
}

function syncWidgetLabels(widget) {
  enforcePurchaseMode(widget);

  const subscriptionToggle = getSubscriptionToggle(widget);

  if (!subscriptionToggle) return;

  const dealBlock = parseJsonAttribute(widget, 'deal-block');
  const dealBars = new Map((dealBlock.dealBars || []).map((dealBar) => [dealBar.id, dealBar]));
  const isSubscribed = subscriptionToggle.getAttribute('aria-checked') === 'true';

  widget.querySelectorAll('.kaching-bundles__bar[data-deal-bar-id]').forEach((bar) => {
    const dealBar = dealBars.get(bar.dataset.dealBarId);
    const title = bar.querySelector('.kaching-bundles__bar-title');
    const subtitle = bar.querySelector('.kaching-bundles__bar-subtitle');

    if (!dealBar || !title || !subtitle) return;

    const labels = isSubscribed
      ? { title: dealBar.title, subtitle: dealBar.subtitle }
      : getOneTimeLabels(widget, dealBar);

    if (title.textContent !== labels.title) title.textContent = labels.title;
    if (subtitle.textContent !== labels.subtitle) subtitle.textContent = labels.subtitle;
  });

  syncRefillNote(widget, dealBars);
}

function scheduleSync(widget) {
  if (scheduledWidgets.has(widget)) return;

  scheduledWidgets.add(widget);
  requestAnimationFrame(() => {
    scheduledWidgets.delete(widget);
    syncWidgetLabels(widget);
  });
}

function initializeWidget(widget) {
  if (initializedWidgets.has(widget)) return;

  initializedWidgets.add(widget);

  const observer = new MutationObserver(() => scheduleSync(widget));
  observer.observe(widget, {
    attributes: true,
    attributeFilter: ['aria-checked', 'class', 'deal-block', 'product'],
    childList: true,
    characterData: true,
    subtree: true,
  });

  scheduleSync(widget);
}

function initializeKachingLabels(root = document) {
  root
    .querySelectorAll(`kaching-bundle[product-id="${getTargetProductId()}"] kaching-bundles-block`)
    .forEach(initializeWidget);
}

initializeKachingLabels();

const widgetDiscoveryObserver = new MutationObserver(() => initializeKachingLabels());
widgetDiscoveryObserver.observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => initializeKachingLabels(event.target));

// Kaching's checked state remains completely app-controlled. For an unchecked
// purchase, stop the BOGO handler before it runs and submit a normal Shopify
// line with the exact 1/2/3-bottle quantity shown on the landing page.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest?.(`${DIRECT_CHECKOUT_FORM_SELECTOR} .phs__btn`);
    const widget = getTargetWidget();

    if (
      !button ||
      !widget ||
      !isOneTimePurchase(widget) ||
      !getSelectedDealBar(widget) ||
      !getSelectedVariant(widget)
    )
      return;

    event.preventDefault();
    event.stopImmediatePropagation();
    goToOneTimeCheckout(button.form, widget);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest?.(DIRECT_CHECKOUT_FORM_SELECTOR);
    const widget = getTargetWidget();

    if (
      !form ||
      !widget ||
      !isOneTimePurchase(widget) ||
      !getSelectedDealBar(widget) ||
      !getSelectedVariant(widget)
    )
      return;

    event.preventDefault();
    event.stopImmediatePropagation();
    goToOneTimeCheckout(form, widget);
  },
  true
);
